import { useEffect, useState } from "react";
import { Phone, Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Logo, WhatsAppIcon } from "./Logo";
import { NAV, PHONE_TEL, PHONE_DISPLAY, WHATSAPP, scrollToId } from "./data";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => { setScrolled(window.scrollY > 24); };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); };
  }, []);

  const go = (id) => { setOpen(false); scrollToId(id); };

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
        scrolled ? "border-b border-black/5 bg-[var(--ivory)]/85 shadow-[0_8px_30px_rgba(15,42,74,0.06)] backdrop-blur-xl" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[76px] max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <Logo />
        <nav className="hidden items-center gap-1 lg:flex">
          {NAV.map((n) => (
            <button
              key={n.id}
              data-testid={`nav-link-${n.id}`}
              onClick={() => go(n.id)}
              className="relative rounded-full px-4 py-2 text-sm font-semibold text-[var(--navy)] transition-colors hover:bg-[var(--navy)]/5"
            >
              {n.label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <a
            href={WHATSAPP}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp-link"
            aria-label="WhatsApp üzenet"
            className="hidden h-11 items-center gap-2 rounded-full border border-[var(--navy)]/15 bg-white px-4 text-sm font-semibold text-[var(--navy)] transition-[transform,background-color] hover:-translate-y-0.5 hover:bg-[#E9F7EF] sm:inline-flex"
          >
            <WhatsAppIcon className="h-5 w-5 text-[#1E9E55]" /> WhatsApp
          </a>
          <a
            href={PHONE_TEL}
            data-testid="header-call-button"
            className="group inline-flex h-11 items-center gap-2 rounded-full bg-[var(--cta)] px-3.5 text-sm font-bold text-white shadow-[0_10px_24px_-8px_rgba(220,68,32,0.7)] transition-[transform,background-color] hover:-translate-y-0.5 hover:bg-[var(--cta-hover)] sm:px-5"
          >
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/70" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-white" />
            </span>
            <Phone className="h-4 w-4" />
            <span className="hidden sm:inline">Hívás Most: {PHONE_DISPLAY}</span>
            <span className="sr-only sm:hidden">Hívás</span>
          </a>
          <button
            data-testid="mobile-menu-toggle"
            aria-label="Menü"
            onClick={() => setOpen((o) => !o)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[var(--navy)]/15 bg-white text-[var(--navy)] lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mx-4 mb-4 rounded-2xl border border-black/5 bg-white p-2 shadow-xl lg:hidden"
          >
            {[...NAV, { id: "ajanlat", label: "Díjmentes Ajánlatkérés" }].map((n) => (
              <button
                key={n.id}
                data-testid={`mobile-nav-${n.id}`}
                onClick={() => go(n.id)}
                className="block w-full rounded-xl px-4 py-3 text-left text-base font-semibold text-[var(--navy)] hover:bg-[var(--ivory)]"
              >
                {n.label}
              </button>
            ))}
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
